import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"

import type { TenderCaseListItemApi } from "@/api/tender-api"

import {
  ArrowRightLeft,
  Briefcase,
  CircleCheck,
  CircleDot,
  CircleX,
  FilePen,
  Loader,
  Search,
  Shield,
  Users,
} from "@/components/icons"
import { AppSidebar } from "@/components/layouts/app-layout/app-sidebar"
import { SidebarNavSection } from "@/components/sidebar/sidebar-nav-section"
import { SidebarSearchResultItem } from "@/components/sidebar/sidebar-search-result-item"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { SidebarGroup } from "@/components/ui/sidebar"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useSidebar } from "@/components/ui/use-sidebar"
import { cn } from "@/lib/utils"

import { TenderAdminModeToggle } from "./tender-admin-mode-toggle"
import { type DerivedStatus, deriveListItemStatus, STATUS_CONFIG } from "./tender-status-utils"
import { useTenderCases } from "../hooks/use-tender-cases"
import { useTenderPaths } from "../hooks/use-tender-paths"
import { TENDER_SIDEBAR_CASES_VISIBLE } from "../tender-sidebar-constants"
import { isInvolvedCase } from "../utils/is-involved-case"
import { limitTenderCasesForSidebar } from "../utils/limit-tender-cases-for-sidebar"

const STATUS_ICON: Record<DerivedStatus, React.ReactNode> = {
  setup: <FilePen className="text-muted-foreground size-[18px] shrink-0" />,
  analyserar_uploading: <Loader className="text-accent size-[18px] shrink-0 animate-spin" />,
  analyserar: <Loader className="text-accent size-[18px] shrink-0 animate-spin" />,
  analyserar_indexing: <Loader className="text-accent size-[18px] shrink-0 animate-spin" />,
  analyserar_extracting: <Loader className="text-accent size-[18px] shrink-0 animate-spin" />,
  analyserar_cv_classifying: <Loader className="text-primary size-[18px] shrink-0 animate-spin" />,
  analyserar_cv_matching: <Loader className="text-primary size-[18px] shrink-0 animate-spin" />,
  // Phase 1 complete (extraction done) but not yet handed off — ready to hand off.
  extrahering: <ArrowRightLeft className="text-warning size-[18px] shrink-0" />,
  klar_overlamning: <ArrowRightLeft className="text-warning size-[18px] shrink-0" />,
  // Phase 2 (handed off) — active handling, not "done". Reserve the green check
  // for the final Go/No-Go decision only.
  cv_matchning: <CircleDot className="text-accent-foreground size-[18px] shrink-0" />,
  anbudsgranskning: <CircleDot className="text-accent-foreground size-[18px] shrink-0" />,
  go: <CircleCheck className="text-success size-[18px] shrink-0" />,
  no_go: <CircleX className="text-destructive size-[18px] shrink-0" />,
}

function AccessIcon({ tenderCase }: { tenderCase: TenderCaseListItemApi }) {
  if (tenderCase.currentUserAccessSource === "admin_default") {
    return <Shield className="text-warning size-2.5" />
  }

  return <Users className="text-accent size-2.5" />
}

function CaseIcon({ tenderCase }: { tenderCase: TenderCaseListItemApi }) {
  const status = deriveListItemStatus(tenderCase)
  const isShared =
    tenderCase.currentUserRole != null && tenderCase.currentUserAccessSource !== "owner"
  const { hasUnviewedNotification } = tenderCase

  return (
    <span className="relative inline-flex">
      {STATUS_ICON[status]}
      {isShared && (
        <span className="absolute -right-1 -bottom-1 flex items-center justify-center">
          <AccessIcon tenderCase={tenderCase} />
        </span>
      )}
      {hasUnviewedNotification && (
        <span className="bg-primary absolute -top-0.5 -right-0.5 size-2 rounded-full" />
      )}
    </span>
  )
}

function NotificationTooltipContent({ tenderCase }: { tenderCase: TenderCaseListItemApi }) {
  const { t } = useTranslation()
  const { invitedByDisplayName, role } = tenderCase.invitationNotification ?? {}
  const roleKey =
    role === "owner" || role === "admin" || role === "editor" || role === "reader"
      ? (`tender.roles.${role}` as const)
      : null
  return (
    <>
      <p className="font-medium">{t("tender.sidebar.notificationTitle")}</p>
      {invitedByDisplayName && (
        <p className="text-muted-foreground mt-0.5">
          {roleKey
            ? t("tender.sidebar.notificationInvitedByRole", {
                name: invitedByDisplayName,
                role: t(roleKey),
              })
            : t("tender.sidebar.notificationInvitedBy", { name: invitedByDisplayName })}
        </p>
      )}
    </>
  )
}

export function TenderSidebar() {
  const { t } = useTranslation()
  const { state, isMobile } = useSidebar()
  const navigate = useNavigate()
  const { basePath, casePath } = useTenderPaths()
  const { caseId } = useParams<{ caseId: string }>()

  // On a case detail page useTenderCase already polls the active run; slow the list
  // poll so we don't stack two 3–5s intervals for the same processing state.
  const { data: allCases = [] } = useTenderCases({
    pollIntervalMs: caseId ? 15_000 : 5_000,
  })
  // The sidebar is a personal worklist: cases are open org-wide, but here we only
  // show the ones the current user is actively involved in. The full list lives
  // on the landing page (with a "My cases" / "All cases" filter).
  const cases = useMemo(() => allCases.filter(isInvolvedCase), [allCases])
  const [search, setSearch] = useState("")
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [collapsedSearch, setCollapsedSearch] = useState("")
  const [showAllInCollapsed, setShowAllInCollapsed] = useState(false)

  const isCollapsed = state === "collapsed" && !isMobile

  const filteredCases = search.trim()
    ? cases.filter((c) => c.title.toLowerCase().includes(search.toLowerCase()))
    : cases

  const collapsedFilteredCases = collapsedSearch.trim()
    ? cases.filter((c) => c.title.toLowerCase().includes(collapsedSearch.toLowerCase()))
    : cases

  const sidebarCasesToRender = useMemo(() => {
    if (search.trim() || showAll) return filteredCases
    return limitTenderCasesForSidebar(filteredCases, TENDER_SIDEBAR_CASES_VISIBLE, caseId)
  }, [caseId, filteredCases, search, showAll])

  const collapsedCasesToRender = useMemo(() => {
    if (collapsedSearch.trim() || showAllInCollapsed) return collapsedFilteredCases
    return limitTenderCasesForSidebar(collapsedFilteredCases, TENDER_SIDEBAR_CASES_VISIBLE, caseId)
  }, [caseId, collapsedFilteredCases, collapsedSearch, showAllInCollapsed])

  const collapsedTotalCount = collapsedFilteredCases.length
  const collapsedVisibleCount = collapsedCasesToRender.length
  const collapsedHasMore =
    collapsedTotalCount > collapsedVisibleCount && !collapsedSearch.trim() && !showAllInCollapsed

  return (
    <AppSidebar>
      <SidebarGroup className="flex flex-col gap-2 p-2">
        {isCollapsed ? (
          <Popover
            open={isPopoverOpen}
            onOpenChange={(open) => {
              setIsPopoverOpen(open)
              if (!open) {
                setCollapsedSearch("")
                setShowAllInCollapsed(false)
              }
            }}
          >
            <PopoverTrigger asChild>
              <Button
                aria-label={t("tender.sidebar.ariaCases")}
                className="hover:bg-sidebar-accent hover:text-sidebar-accent-foreground size-10 rounded-md p-0"
                size="icon"
                type="button"
                variant="ghost"
              >
                <Briefcase className="size-6" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="flex w-72 flex-col p-0"
              side="right"
              sideOffset={8}
            >
              <div className="border-b p-2">
                <div className="mb-2 flex items-center gap-2">
                  <p className="text-sm font-semibold">{t("tender.sidebar.title")}</p>
                  <TenderAdminModeToggle variant="row" />
                </div>
                <div className="relative">
                  <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
                  <Input
                    aria-label={t("tender.sidebar.searchAria")}
                    className="h-8 pl-9 text-sm"
                    placeholder={t("tender.sidebar.searchPlaceholder")}
                    value={collapsedSearch}
                    onChange={(e) => setCollapsedSearch(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Escape" && collapsedSearch && setCollapsedSearch("")
                    }
                  />
                </div>
              </div>
              <div className="relative flex min-h-0 flex-1 flex-col">
                {showAllInCollapsed && (
                  <div className="from-popover pointer-events-none absolute top-0 right-0 left-0 z-10 h-4 bg-gradient-to-b to-transparent" />
                )}
                <div
                  className={cn(
                    "overflow-y-auto",
                    showAllInCollapsed ? "max-h-[300px] pb-10" : "max-h-[300px]"
                  )}
                >
                  <div className="flex flex-col gap-1 p-1">
                    {collapsedFilteredCases.length === 0 && (
                      <p className="text-muted-foreground px-2 py-4 text-center text-xs">
                        {t("tender.sidebar.noResults")}
                      </p>
                    )}
                    {collapsedCasesToRender.map((c) => (
                      <Tooltip key={c.id} open={c.hasUnviewedNotification ? undefined : false}>
                        <TooltipTrigger asChild>
                          <SidebarSearchResultItem
                            className="items-center"
                            icon={<CaseIcon tenderCase={c} />}
                            isActive={c.id === caseId}
                            subtitle={t(STATUS_CONFIG[deriveListItemStatus(c)].labelKey)}
                            title={c.title}
                            onClick={() => {
                              void navigate(casePath(c.id))
                              setIsPopoverOpen(false)
                            }}
                          />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-56 text-xs" side="right">
                          <NotificationTooltipContent tenderCase={c} />
                        </TooltipContent>
                      </Tooltip>
                    ))}
                    {collapsedHasMore && (
                      <Button
                        className="text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground mt-1 h-7 w-full justify-start px-3 text-xs"
                        variant="ghost"
                        onClick={() => setShowAllInCollapsed(true)}
                      >
                        {t("tender.sidebar.seeAll")} ({collapsedTotalCount})
                      </Button>
                    )}
                  </div>
                </div>
                {showAllInCollapsed && !collapsedSearch.trim() && (
                  <div className="pointer-events-none absolute right-0 bottom-0 left-0 z-10 flex flex-col">
                    <div className="from-popover h-6 bg-gradient-to-t to-transparent" />
                    <div className="bg-popover px-1 pb-1">
                      <Button
                        className="text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground pointer-events-auto h-7 w-full justify-start px-3 text-xs"
                        variant="ghost"
                        onClick={() => setShowAllInCollapsed(false)}
                      >
                        {t("tender.sidebar.showLess")}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        ) : (
          <SidebarNavSection
            allItemsShown={showAll}
            className="min-h-0 flex-1"
            defaultExpanded={true}
            forceExpanded={!!search}
            icon={<Briefcase className="size-4" />}
            isSearchActive={isSearchOpen}
            listMaxHeight="max-h-[calc(100svh-14rem)]"
            searchPlaceholder={t("tender.sidebar.searchPlaceholder")}
            searchTerm={search}
            stickyShowLess={true}
            storageKey="sidebar-tender-cases-collapsed"
            title={t("tender.sidebar.title")}
            totalCount={filteredCases.length}
            visibleCount={TENDER_SIDEBAR_CASES_VISIBLE}
            headerActions={
              <div className="flex items-center gap-1">
                <TenderAdminModeToggle variant="icon" />
                <Button
                  aria-label={t("tender.sidebar.searchAria")}
                  size="icon"
                  variant="ghost"
                  className={cn(
                    "hover:bg-foreground/15 size-7 rounded-full p-0",
                    search && "bg-sidebar-accent text-sidebar-accent-foreground"
                  )}
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsSearchOpen(true)
                  }}
                >
                  <Search className="size-4" />
                </Button>
              </div>
            }
            onHeaderClick={() => void navigate(basePath)}
            onSearchChange={setSearch}
            onSeeAllClick={() => setShowAll(true)}
            onShowLessClick={() => setShowAll(false)}
            onSearchClose={() => {
              setIsSearchOpen(false)
              setSearch("")
            }}
          >
            {sidebarCasesToRender.map((c) => (
              <Tooltip key={c.id} open={c.hasUnviewedNotification ? undefined : false}>
                <TooltipTrigger asChild>
                  <SidebarSearchResultItem
                    className="items-center"
                    icon={<CaseIcon tenderCase={c} />}
                    isActive={c.id === caseId}
                    subtitle={t(STATUS_CONFIG[deriveListItemStatus(c)].labelKey)}
                    title={c.title}
                    onClick={() => void navigate(casePath(c.id))}
                  />
                </TooltipTrigger>
                <TooltipContent className="max-w-56 text-xs" side="right">
                  <NotificationTooltipContent tenderCase={c} />
                </TooltipContent>
              </Tooltip>
            ))}
          </SidebarNavSection>
        )}
      </SidebarGroup>
    </AppSidebar>
  )
}
